import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { Toaster } from '../../services/toaster.service';
import { NgIconComponent } from '@ng-icons/core';


@Component({
  selector: 'app-toaster',
  standalone: true,
  imports: [CommonModule,
    NgIconComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './toaster.component.html',
  styleUrls: ['./toaster.component.scss'],
  animations: [
    trigger('slideIn', [
      state('void', style({
        transform: 'translateX(100%)',
        opacity: 0
      })),
      state('*', style({
        transform: 'translateX(0)',
        opacity: 1
      })),
      transition('void => *', animate('300ms ease-out')),
      transition('* => void', animate('300ms ease-in', style({
        transform: 'translateX(100%)',
        opacity: 0
      })))
    ])
  ]
})
export class ToasterComponent implements OnInit, OnDestroy {
  @Input() toaster!: Toaster;
  @Output() dismiss = new EventEmitter<string>();

  progressWidth = 0;
  isPaused = false;
  private startTime: number = 0;
  private remainingTime: number = 0;
  private animationFrameId: number | null = null;

  constructor(private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.startTime = Date.now();
    this.remainingTime = this.toaster.duration || 5000;
    this.startProgressAnimation();
  }

  ngOnDestroy(): void {
    this.stopProgressAnimation();
  }

  /**
   * Start the progress bar animation
   */
  private startProgressAnimation(): void {
    const animate = () => {
      if (this.isPaused) {
        this.animationFrameId = requestAnimationFrame(animate);
        return;
      }

      const currentTime = Date.now();
      const elapsed = currentTime - this.startTime;
      const progress = Math.min((elapsed / this.remainingTime) * 100, 100);

      this.progressWidth = progress;
      this.cdr.detectChanges();

      if (progress >= 100) {
        this.onDismiss();
      } else {
        this.animationFrameId = requestAnimationFrame(animate);
      }
    };

    this.animationFrameId = requestAnimationFrame(animate);
  }

  /**
   * Stop the progress animation
   */
  private stopProgressAnimation(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Pause progress on mouse enter
   */
  onMouseEnter(): void {
    this.isPaused = true;
    this.remainingTime = this.remainingTime - (Date.now() - this.startTime);
  }

  /**
   * Resume progress on mouse leave
   */
  onMouseLeave(): void {
    this.isPaused = false;
    this.startTime = Date.now();
  }

  /**
   * Dismiss the toaster
   */
  onDismiss(): void {
    this.stopProgressAnimation();
    this.dismiss.emit(this.toaster.id);
  }

  /**
   * Handle toaster click
   */
  onToasterClick(event: Event): void {
    // Don't trigger click if clicking the close button
    if ((event.target as HTMLElement).closest('.toaster-close')) {
      return;
    }

    if (this.toaster.clickAction) {
      this.toaster.clickAction();
      this.onDismiss(); // Auto-dismiss after click
    }
  }

  /**
   * Check if toaster has click action
   */
  hasClickAction(): boolean {
    return !!this.toaster.clickAction;
  }

  /** Every toast shows the bell icon, whatever its type. */
  getIconName(): string {
    return 'tablerBell';
  }

  /**
   * Get toaster CSS class based on type
   */
  getToasterClass(): string {
    return `toaster-${this.toaster.type}`;
  }
}
